/**
 * Transport Modes Constants
 * Centralized definitions for transport mode data used across the application
 * This is the single source of truth for transport mode labels, descriptions, and styling
 */

import { TransportMode } from '../dto/provider.types';

// Valid transport modes (ordered: ocean, road, rail - air is excluded/disabled)
export const TRANSPORT_MODES = [
  TransportMode.ocean,
  TransportMode.road,
  TransportMode.rail,
] as const;

// Icons for each transport mode (emoji)
export const TRANSPORT_MODE_ICONS: Record<TransportMode, string> = {
  [TransportMode.ocean]: '🚢',
  [TransportMode.road]: '🚛',
  [TransportMode.rail]: '🚂',
};

// Labels for each transport mode
export const TRANSPORT_MODE_LABELS: Record<TransportMode, string> = {
  [TransportMode.ocean]: 'Ocean',
  [TransportMode.road]: 'Road',
  [TransportMode.rail]: 'Rail',
};

// Labels with icons combined
export const TRANSPORT_MODE_LABELS_WITH_ICONS: Record<TransportMode, string> = {
  [TransportMode.ocean]: '🚢 Ocean',
  [TransportMode.road]: '🚛 Road',
  [TransportMode.rail]: '🚂 Rail',
};

// Descriptions for each transport mode (shown in tooltip)
export const TRANSPORT_MODE_DESCRIPTIONS: Record<TransportMode, string> = {
  [TransportMode.ocean]: 'Sea freight, container shipping, and ocean transport services',
  [TransportMode.road]: 'Trucking, ground transportation, and road freight services',
  [TransportMode.rail]: 'Railway freight, intermodal rail, and rail cargo services',
};

// Colors for map and visualizations (hex)
export const TRANSPORT_MODE_COLORS: Record<TransportMode, string> = {
  [TransportMode.ocean]: '#2563eb', // Blue
  [TransportMode.road]: '#0d9488', // Teal
  [TransportMode.rail]: '#7c3aed', // Purple
};

// Badge/chip colors for UI components (Tailwind classes)
export const TRANSPORT_MODE_BADGE_COLORS: Record<TransportMode, string> = {
  [TransportMode.ocean]: 'bg-blue-50 text-blue-700',
  [TransportMode.road]: 'bg-teal-50 text-teal-700',
  [TransportMode.rail]: 'bg-purple-50 text-purple-700',
};

// Helper function to check if a string is a valid transport mode
export function isValidTransportMode(mode: string): mode is TransportMode {
  return Object.values(TransportMode).includes(mode as TransportMode);
}

// Get icon for a transport mode with fallback
export function getTransportModeIcon(mode: string): string {
  return TRANSPORT_MODE_ICONS[mode as TransportMode] || '📦';
}

// Get label for a transport mode with fallback
export function getTransportModeLabel(mode: string): string {
  return TRANSPORT_MODE_LABELS[mode as TransportMode] || mode;
}

// Get description for a transport mode with fallback
export function getTransportModeDescription(mode: string): string {
  return TRANSPORT_MODE_DESCRIPTIONS[mode as TransportMode] || '';
}

// Get color for a transport mode with fallback
export function getTransportModeColor(mode: string): string {
  return TRANSPORT_MODE_COLORS[mode as TransportMode] || '#6b7280';
}

// Get badge color for a transport mode with fallback
export function getTransportModeBadgeColor(mode: string): string {
  return TRANSPORT_MODE_BADGE_COLORS[mode as TransportMode] || 'bg-gray-50 text-gray-700';
}
